import { HOT_YOTER_STATE_TYPE } from '../constants';

// ---------------------------------------------------------------------------
// User metrics — single place for all eligibility numbers derived from userData
// ---------------------------------------------------------------------------

export const getUserEligiblesMetrics = (userData) => {
    const isUserApprovedSubsidy =
        userData?.isUserApprovedSubsidy
        ?? userData?.is_user_approved_subsidy
        ?? userData?.consentedToSubsidy;

    const numOfEligibles = userData?.numOfEligibles ?? 0;
    const numOfEligiblesLeft = userData?.numOfEligiblesLeft ?? 0;
    const numOfGoldEligibles = userData?.numOfGoldEligibles ?? 0;
    const numOfGoldEligiblesLeft = userData?.numOfGoldEligiblesLeft ?? 0;

    const hasUnusedGold = numOfGoldEligibles > 0 && numOfGoldEligiblesLeft > 0;
    const hasExtendedEligibles = numOfEligibles > 2 || numOfGoldEligibles > 1;
    const isGoldOnlyPlan = numOfEligibles === 0 && numOfGoldEligibles > 0;
    const effectiveNumOfEligibles = hasExtendedEligibles
        ? numOfEligiblesLeft
        : numOfEligibles;
    // Standard plan: gold is only purchasable while 2 regular eligibilities remain.
    // Extended plan: unused gold is shown even when regular eligibilities are partially used.
    // Gold-only plan: no regular eligibilities — show gold directly.
    const canUseGoldEligibility = hasUnusedGold
        && (hasExtendedEligibles || numOfEligiblesLeft >= 2 || isGoldOnlyPlan);

    return {
        isUserApprovedSubsidy,
        numOfEligibles,
        numOfEligiblesLeft,
        numOfGoldEligibles,
        numOfGoldEligiblesLeft,
        hasUnusedGold,
        hasExtendedEligibles,
        isGoldOnlyPlan,
        effectiveNumOfEligibles,
        canUseGoldEligibility,
    };
};

// ---------------------------------------------------------------------------
// State resolution — ordered rules, first match wins (easy to reorder / tweak)
// ---------------------------------------------------------------------------

const STATE_RESOLUTION_RULES = [
    {
        state: HOT_YOTER_STATE_TYPE.HAS_NOT_AGREED_SUBSIDY_PLAN,
        when: ({ isUserApprovedSubsidy }) => isUserApprovedSubsidy === false,
    },
    {
        state: HOT_YOTER_STATE_TYPE.NOT_YET,
        when: ({ numOfEligibles, hasUnusedGold }) =>
            numOfEligibles === 0 && !hasUnusedGold,
    },
    {
        // Extended plan: show gold while unused.
        // Standard plan: only while 2 regular eligibilities remain (gold isn't purchasable after).
        state: HOT_YOTER_STATE_TYPE.GOT_GOLD,
        when: ({ canUseGoldEligibility }) => canUseGoldEligibility,
    },
    {
        state: HOT_YOTER_STATE_TYPE.LEFT_ZERO,
        when: ({ numOfEligiblesLeft }) => numOfEligiblesLeft === 0,
    },
    {
        state: HOT_YOTER_STATE_TYPE.GOT_TWO,
        when: ({ numOfEligiblesLeft }) => numOfEligiblesLeft >= 2,
    },
    {
        state: HOT_YOTER_STATE_TYPE.LEFT_ONE,
        when: ({ numOfEligiblesLeft, canUseGoldEligibility, effectiveNumOfEligibles }) =>
            numOfEligiblesLeft === 1
            && !canUseGoldEligibility
            && effectiveNumOfEligibles === 2,
    },
    {
        state: HOT_YOTER_STATE_TYPE.GOT_ONE,
        when: ({ numOfEligiblesLeft, canUseGoldEligibility }) =>
            numOfEligiblesLeft === 1 && !canUseGoldEligibility,
    },
];

export const resolveHotYoterStateType = (userData) => {
    const metrics = getUserEligiblesMetrics(userData);
    return STATE_RESOLUTION_RULES.find((rule) => rule.when(metrics))?.state ?? null;
};

// ---------------------------------------------------------------------------
// Per-state UI config — badge, visual state, default action type
// ---------------------------------------------------------------------------

const STATE_UI_CONFIG = {
    [HOT_YOTER_STATE_TYPE.NOT_YET]: {
        uiState: 'empty',
        badgeCount: 0,
        actionType: 'details',
    },
    [HOT_YOTER_STATE_TYPE.HAS_NOT_AGREED_SUBSIDY_PLAN]: {
        uiState: 'empty',
        badgeCount: 0,
        actionType: 'subsidy_consent',
    },
    [HOT_YOTER_STATE_TYPE.LEFT_ZERO]: {
        uiState: 'all_used',
        badgeCount: 0,
        actionType: 'choose_benefit',
    },
    [HOT_YOTER_STATE_TYPE.LEFT_ONE]: {
        uiState: 'partial',
        getBadgeCount: ({ numOfEligiblesLeft }) => numOfEligiblesLeft || 1,
        actionType: 'choose_benefit',
    },
    [HOT_YOTER_STATE_TYPE.GOT_GOLD]: {
        uiState: 'partial',
        badgeCount: 'gold',
        actionType: 'choose_benefit',
    },
    [HOT_YOTER_STATE_TYPE.GOT_ONE]: {
        uiState: 'won',
        badgeCount: 1,
        actionType: 'choose_benefit',
    },
    [HOT_YOTER_STATE_TYPE.GOT_TWO]: {
        uiState: 'won',
        badgeCount: 2,
        actionType: 'choose_benefit',
    },
};

const getBadgeForStateType = (stateType, metrics) => {
    const config = STATE_UI_CONFIG[stateType];
    if (!config) {
        return 0;
    }
    if (typeof config.getBadgeCount === 'function') {
        return config.getBadgeCount(metrics);
    }
    return config.badgeCount ?? 0;
};

const getUiStateForStateType = (stateType) =>
    STATE_UI_CONFIG[stateType]?.uiState ?? 'won';

const getActionTypeForStateType = (stateType, actionLabel) => {
    if (!actionLabel) {
        return null;
    }
    return STATE_UI_CONFIG[stateType]?.actionType ?? 'choose_benefit';
};

// ---------------------------------------------------------------------------
// CMS text lookup
// ---------------------------------------------------------------------------

const emptyContent = (state) => ({
    state,
    badgeCount: 0,
    title: '',
    subtitle: '',
    actionLabel: null,
    actionType: null,
});

const normalizeStateKey = (state) =>
    String(state ?? '').trim().toLowerCase();

const getEntryText = (entry, ...keys) => {
    for (const key of keys) {
        const value = entry?.[key];

        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return '';
};

const findStateTextEntry = (stateTexts, stateType) => {
    if (!stateTexts || !stateType) {
        return null;
    }

    const normalizedStateType = normalizeStateKey(stateType);

    if (Array.isArray(stateTexts)) {
        return stateTexts.find(
            (item) => normalizeStateKey(item?.state ?? item?.State) === normalizedStateType,
        ) ?? null;
    }

    if (stateTexts[normalizedStateType] || stateTexts[stateType]) {
        return stateTexts[normalizedStateType] ?? stateTexts[stateType];
    }

    const matchedKey = Object.keys(stateTexts).find(
        (key) => normalizeStateKey(key) === normalizedStateType,
    );

    return matchedKey ? stateTexts[matchedKey] : null;
};

const getEntryButtonText = (entry) =>
    getEntryText(entry, 'buttonText', 'button_text', 'ButtonText');

const getGoldSubtitle = (subtitle, metrics, translations) => {
    if (metrics.numOfGoldEligibles === 2 && metrics.numOfGoldEligiblesLeft === 1) {
        return translations?.['hotyoter_gold_panel_subtitle_after_update'];
    }
    return subtitle;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const canViewPurchasedHotYoterBenefits = (userData) => {
    const {
        numOfEligibles,
        numOfEligiblesLeft,
        numOfGoldEligibles,
        numOfGoldEligiblesLeft,
        hasExtendedEligibles,
    } = getUserEligiblesMetrics(userData);

    const hasRemainingEligibles = numOfEligiblesLeft > 0 || numOfGoldEligiblesLeft > 0;
    if (hasExtendedEligibles && hasRemainingEligibles) {
        return false;
    }

    return (
        numOfEligiblesLeft < numOfEligibles
        || numOfGoldEligiblesLeft < numOfGoldEligibles
    );
};

export const getEligiblesContent = (stateTexts, userData, pageStateType, translations) => {
    const metrics = getUserEligiblesMetrics(userData);
    const stateType = pageStateType ?? resolveHotYoterStateType(userData);

    if (!stateType) {
        return emptyContent('all_used');
    }

    const entry = findStateTextEntry(stateTexts, stateType);
    if (!entry) {
        return emptyContent(getUiStateForStateType(stateType));
    }

    const actionLabel = getEntryButtonText(entry);
    const subtitle = getEntryText(entry, 'subtitle');


    return {
        state: getUiStateForStateType(stateType),
        badgeCount: getBadgeForStateType(stateType, metrics),
        title: getEntryText(entry, 'title'),
        subtitle: stateType === HOT_YOTER_STATE_TYPE.GOT_GOLD
            ? getGoldSubtitle(subtitle, metrics, translations)
            : subtitle,
        actionLabel,
        actionType: getActionTypeForStateType(stateType, actionLabel),
    };
};
